export type CommunicationAudienceFilters = {
  ticketIds?: string[];
  invitationIds?: string[];
  participantGroupIds?: string[];
  manualProfileIds?: string[];
  includeEntityTeamMembers?: boolean;
  includeRegisteredAttendees?: boolean;
};

export type EntityAudienceMember = {
  email?: string | null;
  team: Array<{
    id: string;
    email?: string | null;
    profileId?: string | null;
  }>;
};

export type RegisteredAttendee = {
  profileId: string;
  ticketIds: string[];
  invitationIds: string[];
  participantGroupIds: string[];
};

export function uniqueIds(ids: Array<string | null | undefined>): string[] {
  return [...new Set(ids.filter((id): id is string => Boolean(id)))];
}

/** Invitee contact on the line, else the buyer. Empty items still include the buyer. */
export function orderCompletedProfileIds(
  buyerProfileId: string | null | undefined,
  itemContactIds: Array<string | null | undefined>
): string[] {
  const fromItems = itemContactIds.map(
    (contactId) => contactId ?? buyerProfileId
  );
  return uniqueIds(fromItems.length ? fromItems : [buyerProfileId]);
}

export function hasSelected(ids?: string[]): boolean {
  return Boolean(ids?.length);
}

/** Same contact rule as the participant-group directory: email match, else first team member. */
export function pickEntityAudienceProfileIds(
  entities: EntityAudienceMember[],
  includeTeamMembers: boolean
): string[] {
  const ids: string[] = [];
  for (const entity of entities) {
    if (includeTeamMembers) {
      ids.push(
        ...entity.team.flatMap((member) =>
          member.profileId ? [member.profileId] : []
        )
      );
      continue;
    }
    const email = entity.email?.trim().toLowerCase();
    const contact =
      (email &&
        entity.team.find(
          (member) => member.email?.trim().toLowerCase() === email
        )) ||
      entity.team[0];
    if (contact?.profileId) ids.push(contact.profileId);
  }
  return uniqueIds(ids);
}

export function isEntityAudienceContact(
  entity: EntityAudienceMember,
  userId: string | null | undefined,
  includeTeamMembers: boolean
): boolean {
  if (!userId) return false;
  if (includeTeamMembers) {
    return entity.team.some((member) => member.id === userId);
  }
  const email = entity.email?.trim().toLowerCase();
  const contact =
    (email &&
      entity.team.find(
        (member) => member.email?.trim().toLowerCase() === email
      )) ||
    entity.team[0];
  return contact?.id === userId;
}

/** Empty category imposes no condition. Non-empty is OR within the category. */
export function categoryMatches(
  selected: string[] | undefined,
  values: string[]
): boolean {
  if (!selected?.length) return true;
  const wanted = new Set(selected);
  return values.some((value) => wanted.has(value));
}

export function includeRegisteredPool(
  filters: CommunicationAudienceFilters
): boolean {
  return filters.includeRegisteredAttendees !== false;
}

/**
 * Buyers are included when a ticket/invitation filter is set, or when no
 * group filter is set (default = completed-order attendees).
 */
export function includeBuyerPool(
  filters: CommunicationAudienceFilters
): boolean {
  if (!includeRegisteredPool(filters)) return false;
  return (
    hasSelected(filters.ticketIds) ||
    hasSelected(filters.invitationIds) ||
    !hasSelected(filters.participantGroupIds)
  );
}

export function includeGroupPool(
  filters: CommunicationAudienceFilters
): boolean {
  return (
    includeRegisteredPool(filters) && hasSelected(filters.participantGroupIds)
  );
}

/** Ticket AND invitation. Empty category = no restriction. */
export function buyerMatchesFilters(
  attendee: RegisteredAttendee,
  filters: CommunicationAudienceFilters
): boolean {
  return (
    categoryMatches(filters.ticketIds, attendee.ticketIds) &&
    categoryMatches(filters.invitationIds, attendee.invitationIds)
  );
}

export function groupMatchesFilters(
  groupIds: string[],
  filters: CommunicationAudienceFilters
): boolean {
  return (
    includeGroupPool(filters) &&
    categoryMatches(filters.participantGroupIds, groupIds)
  );
}

/**
 * Union of sources: buyer pool (completed orders) and/or group pool
 * (uploaded members included). Empty filters → buyers only.
 */
export function profileMatchesSources(
  attendee: RegisteredAttendee | null,
  groupIds: string[],
  filters: CommunicationAudienceFilters
): boolean {
  if (groupMatchesFilters(groupIds, filters)) return true;
  if (!includeBuyerPool(filters) || !attendee) return false;
  return buyerMatchesFilters(attendee, filters);
}

export function profileMatchesFilters(
  attendee: RegisteredAttendee,
  filters: CommunicationAudienceFilters
): boolean {
  return profileMatchesSources(attendee, attendee.participantGroupIds, filters);
}

/** Buyer ∪ group members first, then additive manual IDs, then dedupe. */
export function mergeAudienceIds(
  registeredIds: string[],
  manualIds: string[] | undefined
): string[] {
  return uniqueIds([...registeredIds, ...(manualIds ?? [])]);
}

/** Prefer the event profile owned by the WL login user for that email. */
export function canonicalizeAudienceIds(
  profiles: Array<{ id: string; email?: string | null }>,
  loginProfileIdByEmail: Record<string, string>,
  allIds?: string[]
): string[] {
  const loginIdByProfileId = new Map(
    profiles.map((profile) => {
      const email = profile.email?.trim().toLowerCase();
      return [
        profile.id,
        (email && loginProfileIdByEmail[email]) || profile.id,
      ];
    })
  );
  const source = allIds ?? profiles.map((profile) => profile.id);
  return uniqueIds(source.map((id) => loginIdByProfileId.get(id) ?? id));
}

export function countAudienceOverlap(
  buyerIds: string[],
  groupMemberIds: string[]
): number {
  if (buyerIds.length === 0 || groupMemberIds.length === 0) return 0;
  const buyers = new Set(buyerIds);
  let overlap = 0;
  for (const id of groupMemberIds) {
    if (buyers.has(id)) overlap += 1;
  }
  return overlap;
}

/**
 * Occurrence-time matcher: manual IDs always qualify; otherwise union of
 * buyer + group sources. `groupIds` covers uploaded profiles with no order.
 */
export function attendeeMatchesCampaign(
  profileId: string,
  attendee: RegisteredAttendee | null,
  filters: CommunicationAudienceFilters,
  groupIds: string[] = []
): boolean {
  if (filters.manualProfileIds?.includes(profileId)) return true;
  if (attendee && attendee.profileId !== profileId) return false;
  return profileMatchesSources(
    attendee,
    attendee?.participantGroupIds.length
      ? attendee.participantGroupIds
      : groupIds,
    filters
  );
}

export function collectAttendeeFromItems(
  profileId: string,
  items: Array<{
    ticketId?: string | null;
    invitationId?: string | null;
  }>,
  participantGroupIds: string[] = []
): RegisteredAttendee {
  return {
    profileId,
    ticketIds: uniqueIds(items.map((item) => item.ticketId)),
    invitationIds: uniqueIds(items.map((item) => item.invitationId)),
    participantGroupIds: uniqueIds(participantGroupIds),
  };
}
